import React, { useState } from 'react';
import { Toast } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import { submitHelpSupportQuery } from '../../api/help-support.api';
import { sendGenericWebViewAction } from '../../utils';
import arrowLeft from '../../icons/arrowLeft.svg';
import { NAMESPACE } from './i18n';
import LanguageSelector from './LanguageSelector';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{7,15}$/;
const MAX_QUERY_LENGTH = 2000;

interface FormState {
    name: string;
    email: string;
    phone: string;
    query: string;
}

// Values are English source strings (i18n keys) — translated at render so
// errors already on screen re-render when the language changes.
interface FormErrors {
    name?: string;
    email?: string;
    phone?: string;
    query?: string;
}

const HelpAndSupport: React.FC = () => {
    const { t } = useTranslation(NAMESPACE);
    const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', query: '' });
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const validate = (): FormErrors => {
        const errs: FormErrors = {};
        if (!form.name.trim()) {
            errs.name = 'Name is required';
        }
        if (!form.email.trim()) {
            errs.email = 'Email is required';
        } else if (!EMAIL_REGEX.test(form.email.trim())) {
            errs.email = 'Enter a valid email address';
        }
        if (!form.phone.trim()) {
            errs.phone = 'Phone number is required';
        } else if (!PHONE_REGEX.test(form.phone.trim())) {
            errs.phone = 'Enter a valid phone number (digits only, 7-15 digits)';
        }
        if (!form.query.trim()) {
            errs.query = 'Please describe your query';
        } else if (form.query.length > MAX_QUERY_LENGTH) {
            errs.query = 'Query must be under {{max}} characters';
        }
        return errs;
    };

    const handleBack = () => {
        sendGenericWebViewAction('BACK_PRESS', {});
    };

    const handleChange = (field: keyof FormState) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setSubmitting(true);
        try {
            const response = await submitHelpSupportQuery({
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                query: form.query.trim(),
            });
            if (response.isSuccess) {
                setSubmitted(true);
            } else {
                Toast.show({
                    content: response.errorMessage || t('Submission failed. Please try again.'),
                    icon: 'fail',
                    duration: 3000,
                });
            }
        } catch (err) {
            Toast.show({
                content: (err as Error).message || t('Submission failed. Please try again.'),
                icon: 'fail',
                duration: 3000,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const css = `
        .hs-page{min-height:100vh;background:#f8fafc;font-family:system-ui,-apple-system,'Segoe UI',Roboto,Inter,sans-serif}
        .hs-navbar{position:sticky;top:0;z-index:100;display:flex;align-items:center;gap:8px;padding:14px 12px;background:#e2231a;box-shadow:0 1px 4px rgba(0,0,0,.12)}
        .hs-back{display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;padding:0;border:0;background:transparent;cursor:pointer;border-radius:8px}
        .hs-back:active{background:rgba(255,255,255,.18)}
        .hs-back img{width:22px;height:22px;display:block;filter:brightness(0) invert(1)}
        [dir="rtl"] .hs-back img{transform:scaleX(-1)}
        .hs-navtitle{font-size:18px;font-weight:700;color:#fff;letter-spacing:-0.01em}
        .hs-lang{position:relative;display:inline-flex;align-items:center;gap:5px;margin-inline-start:auto;padding:6px 10px;border-radius:8px;background:rgba(255,255,255,.14)}
        .hs-lang-globe{display:block}
        .hs-lang-code{font-size:13px;font-weight:700;color:#fff;letter-spacing:.02em}
        .hs-lang-caret{font-size:10px;color:#fff}
        .hs-lang-select{position:absolute;inset:0;width:100%;height:100%;opacity:0;border:0;cursor:pointer}
        .hs-wrap{max-width:430px;margin:0 auto;padding:24px 16px 40px}
        .hs-header{margin-bottom:20px}
        .hs-title{font-size:20px;font-weight:700;color:#111827;margin:0 0 4px}
        .hs-subtitle{font-size:13px;color:#6b7280;margin:0}
        .hs-card{background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:20px;box-shadow:0 1px 3px rgba(16,24,40,.06)}
        .hs-field{display:flex;flex-direction:column;gap:5px;margin-bottom:16px}
        .hs-label{font-size:13px;font-weight:500;color:#374151}
        .hs-asterisk{color:#e2231a;margin-inline-start:2px}
        .hs-input{display:block;width:100%;height:44px;border:1px solid #d1d5db;border-radius:8px;padding:0 12px;font-size:14px;color:#111827;box-sizing:border-box;outline:none}
        .hs-input:focus{border-color:#e2231a;box-shadow:0 0 0 2px rgba(226,35,26,.15)}
        .hs-input.error{border-color:#e2231a}
        .hs-textarea{display:block;width:100%;border:1px solid #d1d5db;border-radius:8px;padding:10px 12px;font-size:14px;color:#111827;resize:vertical;min-height:120px;box-sizing:border-box;outline:none;font-family:inherit}
        .hs-textarea:focus{border-color:#e2231a;box-shadow:0 0 0 2px rgba(226,35,26,.15)}
        .hs-textarea.error{border-color:#e2231a}
        .hs-char-row{display:flex;justify-content:space-between;align-items:center;margin-top:3px}
        .hs-error{font-size:12px;color:#e2231a}
        .hs-char-count{font-size:12px;color:#9ca3af}
        .hs-char-count.over{color:#e2231a}
        .hs-submit{width:100%;height:48px;border-radius:10px;border:0;background:#e2231a;color:#fff;font-size:15px;font-weight:700;cursor:pointer;margin-top:4px}
        .hs-submit:disabled{background:#f1a9a4;cursor:not-allowed}
        .hs-success{display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:48px 16px;gap:16px}
        .hs-success-icon{width:64px;height:64px;border-radius:50%;background:#fde8e7;color:#e2231a;display:flex;align-items:center;justify-content:center;font-size:28px}
        .hs-success-title{font-size:20px;font-weight:700;color:#111827;margin:0}
        .hs-success-msg{font-size:14px;color:#6b7280;margin:0;line-height:1.5}
    `;

    if (submitted) {
        return (
            <div className="hs-page">
                <style>{css}</style>
                <div className="hs-navbar">
                    <button type="button" className="hs-back" onClick={handleBack} aria-label={t('Go back')}>
                        <img src={arrowLeft} alt="" />
                    </button>
                    <span className="hs-navtitle">{t('Help & Support')}</span>
                    <LanguageSelector />
                </div>
                <div className="hs-wrap">
                    <div className="hs-success">
                        <div className="hs-success-icon">✓</div>
                        <p className="hs-success-title">{t('Query Submitted')}</p>
                        <p className="hs-success-msg">
                            {t('Thank you for reaching out. Our support team will get back to you shortly on the email address you provided.')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const queryOverLimit = form.query.length > MAX_QUERY_LENGTH;

    return (
        <div className="hs-page">
            <style>{css}</style>
            <div className="hs-navbar">
                <button type="button" className="hs-back" onClick={handleBack} aria-label={t('Go back')}>
                    <img src={arrowLeft} alt="" />
                </button>
                <span className="hs-navtitle">{t('Help & Support')}</span>
                <LanguageSelector />
            </div>
            <div className="hs-wrap">
            <div className="hs-header">
                <p className="hs-subtitle">{t("Fill in the details below and we'll get back to you")}</p>
            </div>

            <div className="hs-card">
                <form onSubmit={handleSubmit} noValidate>
                    <div className="hs-field">
                        <label className="hs-label">
                            {t('Name')}<span className="hs-asterisk">*</span>
                        </label>
                        <input
                            className={`hs-input${errors.name ? ' error' : ''}`}
                            type="text"
                            placeholder={t('Enter your full name')}
                            value={form.name}
                            onChange={handleChange('name')}
                            disabled={submitting}
                        />
                        {errors.name && <span className="hs-error">{t(errors.name)}</span>}
                    </div>

                    <div className="hs-field">
                        <label className="hs-label">
                            {t('Email ID')}<span className="hs-asterisk">*</span>
                        </label>
                        <input
                            className={`hs-input${errors.email ? ' error' : ''}`}
                            type="email"
                            placeholder={t('Enter your email address')}
                            value={form.email}
                            onChange={handleChange('email')}
                            disabled={submitting}
                        />
                        {errors.email && <span className="hs-error">{t(errors.email)}</span>}
                    </div>

                    <div className="hs-field">
                        <label className="hs-label">
                            {t('Phone Number')}<span className="hs-asterisk">*</span>
                        </label>
                        <input
                            className={`hs-input${errors.phone ? ' error' : ''}`}
                            type="tel"
                            placeholder={t('Enter your phone number')}
                            value={form.phone}
                            onChange={handleChange('phone')}
                            disabled={submitting}
                        />
                        {errors.phone && <span className="hs-error">{t(errors.phone)}</span>}
                    </div>

                    <div className="hs-field">
                        <label className="hs-label">
                            {t('Your Query')}<span className="hs-asterisk">*</span>
                        </label>
                        <textarea
                            className={`hs-textarea${errors.query ? ' error' : ''}`}
                            placeholder={t('Describe your issue or question...')}
                            value={form.query}
                            onChange={handleChange('query')}
                            disabled={submitting}
                        />
                        <div className="hs-char-row">
                            <span className="hs-error">
                                {errors.query ? t(errors.query, { max: MAX_QUERY_LENGTH }) : ''}
                            </span>
                            <span className={`hs-char-count${queryOverLimit ? ' over' : ''}`}>
                                {form.query.length}/{MAX_QUERY_LENGTH}
                            </span>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="hs-submit"
                        disabled={submitting || queryOverLimit}
                    >
                        {submitting ? t('Submitting...') : t('Submit Query')}
                    </button>
                </form>
            </div>
            </div>
        </div>
    );
};

export default HelpAndSupport;
